import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { AsyncErrorHandler } from 'lib/ui';
import { Models } from 'models/core';
import { CreatePenOrderDto, PenIdentifierDto } from 'models/pen/PenOrder';
import { PenOrderService } from 'services/pen/pen-order-service';
import { PenOfferService } from 'services/pen/pen-offer-service';
import { ProducerService } from 'services/producer-service';
import { StandardValidationBuilder } from 'lib/validation';
import { ValidationController } from 'aurelia-validation';
import { ToastService } from 'services';
import { GlobalAnalysisService } from 'services/pen/global-analysis-service';

@autoinject
export class PenOfferCreateOrder {
  protected penOfferId: number;
  protected createPenOrderDto: CreatePenOrderDto = new CreatePenOrderDto();
  protected penId: number;

  protected canEditProducers: boolean = false;
  protected producers: Array<Models.Producer>;

  protected penIdentifiers: Array<PenIdentifierDto> = [];

  constructor(
    private producerService: ProducerService,
    private router: Router,
    private penOrderService: PenOrderService,
    private penOfferService: PenOfferService,
    private t: I18N,
    private toastService: ToastService,
    private ruleBuilder: StandardValidationBuilder,
    protected validationController: ValidationController,
    private globalAnalysisService: GlobalAnalysisService
  ) {}

  private async activate(params) {
    this.penOfferId = params.penOfferId;
    this.penId = params.penId;

    await this.getProducers();
    await this.getPenOffer();
    //Set to Mørenot Aquaculture and lock the field for now
    this.createPenOrderDto.ProducerId = 51;

    this.createValidationRules();
  }

  @AsyncErrorHandler
  async getProducers() {
    this.producers = await this.producerService.getAll('$filter=IsMorenotCompany eq true');
    return this.producers;
  }

  @AsyncErrorHandler
  private async getPenOffer() {
    const offer = await this.penOfferService.getOffer(this.penOfferId);
    this.penIdentifiers = Array.from({ length: offer?.data?.NumberOfPens }, (_, index) => ({Id: index, Value: '', IsManual: false, IsFirst: index === 0}));
    this.penIdentifiers.forEach((item) => this.addPenIdentifierValidationRules(item));
  }

  private addPenIdentifierValidationRules(item: { Id: number; Value: string; IsManual: boolean; IsFirst: boolean }) {
    if (item.IsFirst) return;
    this.ruleBuilder
      .with(item, item.Id.toString())
      .required('Value', (m) => m.IsManual)
      .custom(
        'Value',
        this.t.tr('validation.ExactLengthOfX', { value: 5 }),
        (value) => /^\d{5}$/.test(String(value ?? '')),
        (m) => m.IsManual
      )
      .done();
  }

  @AsyncErrorHandler
  async createPenOrder() {
    const { valid } = await this.validationController.validate();
    if (!valid) return;

    this.createPenOrderDto.PenOfferId = this.penOfferId;
    this.createPenOrderDto.PenIdentifiers = this.penIdentifiers;
    const { data } = await this.penOrderService.createPenOrder(this.createPenOrderDto);

    this.toastService.showSuccess(this.t.tr('general.created'));
    this.router.navigate(`pen-order/${data.PenOrderId}/pen/${data.PenId}`);
  }

  private createValidationRules() {
    this.ruleBuilder.with(this.createPenOrderDto).required('OrderNumber').required('PenIdStart').custom('PenIdStart', this.t.tr('validation.ExactLengthOfX', { value: 5 }), (value) => /^\d{5}$/.test(String(value ?? ''))).required('GlobalAnalysisId').done();
  }

  setAndCalcualtePenIdentifiers(penIdStart: number) {    
    this.penIdentifiers[0].Value = penIdStart ? String(penIdStart) : '';
    this.createPenOrderDto.PenIdStart = penIdStart;

    if (penIdStart === null || penIdStart === undefined || !/^\d{5}$/.test(String(penIdStart))) {
      return;
    }

    let index = 1;
    this.penIdentifiers.forEach(element => {
      if(!element.IsFirst && !element.IsManual) {
        element.Value = (Number(penIdStart) + index).toString();
        index++;
      }
    });
  }
}
